import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Ambulance, LogOut, MapPin, Clock, Play, CheckCircle2, XCircle, Radio, ClipboardCheck, Star, KeyRound } from "lucide-react";
import RatingsModal from "@/components/RatingsModal";

const STATUS_CLS = {
  assigned:    "bg-sky-50 text-sky-800 border-sky-200",
  in_progress: "bg-indigo-50 text-indigo-800 border-indigo-200",
  completed:   "bg-emerald-50 text-emerald-800 border-emerald-200",
};

// The old floating signature card, the docs drawer opened from here, and the license/
// application/agreements/W-9 link lines all moved to their own page, DriverDocuments.jsx
// (contract I), reachable from the single "My documents" button below.
export default function DriverPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [tab, setTab] = useState("active");
  const [busyId, setBusyId] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [ratingsRideId, setRatingsRideId] = useState(null);
  const watchIdRef = useRef(null);
  const lastPingAtRef = useRef(0);

  const load = () =>
    api.get("/driver/rides").then((r) => setRides(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const active = useMemo(() => rides.filter((r) => r.status === "assigned" || r.status === "in_progress"), [rides]);
  const completed = useMemo(() => rides.filter((r) => r.status === "completed"), [rides]);
  const inProgress = useMemo(() => rides.find((r) => r.status === "in_progress"), [rides]);

  // Live GPS ping while a ride is in_progress
  useEffect(() => {
    const stop = () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setTracking(false);
    };
    if (!inProgress) { stop(); return; }
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    if (watchIdRef.current != null) return;
    const id = navigator.geolocation.watchPosition(
      async (pos) => {
        const now = Date.now();
        if (now - lastPingAtRef.current < 15000) return; // 15s throttle
        lastPingAtRef.current = now;
        try {
          await api.post(`/driver/rides/${inProgress.id}/ping`, {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            speed: pos.coords.speed ?? null,
            heading: pos.coords.heading ?? null,
          });
          setTracking(true);
        } catch (err) {
          console.warn("ping failed", err);
        }
      },
      (err) => { console.warn("gps error", err); setTracking(false); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
    watchIdRef.current = id;
    return stop;
  }, [inProgress]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/driver/rides/${id}/status`, { status });
      toast.success(status === "completed" ? "Marked complete" : status === "in_progress" ? "Trip started · GPS on" : "Updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const list = tab === "active" ? active : completed;

  const fmt = (dt) => {
    if (!dt) return "";
    const d = new Date(dt);
    if (isNaN(d)) return dt;
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#005E82] flex items-center justify-center"><Ambulance size={18} /></div>
            <div>
              <div className="font-display font-semibold text-sm">Driver Portal</div>
              <div className="text-[11px] text-slate-400 truncate max-w-[180px]">{user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {inProgress && (
              <span
                className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full border ${tracking ? "bg-emerald-500/20 text-emerald-100 border-emerald-400/40" : "bg-amber-500/20 text-amber-100 border-amber-400/40"}`}
                data-testid="gps-badge"
              >
                <Radio size={11} className={tracking ? "animate-pulse" : ""} />
                {tracking ? "GPS live" : "GPS waiting…"}
              </span>
            )}
            <button onClick={() => navigate("/driver/documents")} className="text-slate-300 text-xs flex items-center gap-1.5 hover:text-white" data-testid="driver-docs">
              <ClipboardCheck size={14} /> My documents
            </button>
            <button onClick={() => navigate("/driver/account")} className="text-slate-300 text-xs flex items-center gap-1.5 hover:text-white" data-testid="driver-account">
              <KeyRound size={14} /> Password
            </button>
            <button onClick={logout} className="text-slate-300 text-xs flex items-center gap-1.5 hover:text-white" data-testid="driver-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 pt-5 pb-24" data-testid="driver-portal">
        <div className="grid grid-cols-2 gap-2 bg-white p-1 rounded-md border border-slate-200 mb-4">
          <button
            onClick={() => setTab("active")}
            className={`py-2 text-sm rounded-md ${tab === "active" ? "bg-[#0A192F] text-white" : "text-slate-600"}`}
            data-testid="tab-active"
          >
            Today ({active.length})
          </button>
          <button
            onClick={() => setTab("completed")}
            className={`py-2 text-sm rounded-md ${tab === "completed" ? "bg-[#0A192F] text-white" : "text-slate-600"}`}
            data-testid="tab-completed"
          >
            Completed ({completed.length})
          </button>
        </div>

        {list.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-sm text-slate-500">
            {tab === "active" ? "No trips assigned yet." : "No completed trips yet."}
          </div>
        )}

        <div className="space-y-3">
          {list.map((r) => (
            <article key={r.id} className="bg-white border border-slate-200 rounded-md p-4 fade-in" data-testid={`driver-ride-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-semibold text-slate-900">{r.patient_name}</div>
                  <div className="text-xs text-slate-500 capitalize mt-0.5">{r.mobility} · {r.trip_type.replace("_", " ")}</div>
                </div>
                <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 border capitalize ${STATUS_CLS[r.status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{r.status.replace("_", " ")}</span>
              </div>

              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <Clock size={14} className="text-slate-400 mt-0.5" />
                  <span className="font-mono text-xs">{fmt(r.pickup_datetime)}</span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="text-emerald-600 mt-0.5" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pickup</div>
                    <div className="text-slate-800">{r.pickup_address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="text-rose-600 mt-0.5" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Dropoff</div>
                    <div className="text-slate-800">{r.dropoff_address}</div>
                    {r.dropoff_phone && (
                      <div className="text-xs mt-0.5" data-testid={`driver-dropoff-phone-${r.id}`}>
                        <span className="text-slate-500">Destination contact: </span>
                        <a href={`tel:${r.dropoff_phone}`} className="text-[#005E82] hover:underline">{r.dropoff_phone}</a>
                      </div>
                    )}
                  </div>
                </div>
                {r.notes && (
                  <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-2 mt-2">
                    {r.notes}
                  </div>
                )}
              </div>

              {r.status !== "completed" && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {r.status === "assigned" && (
                    <button
                      onClick={() => updateStatus(r.id, "in_progress")}
                      disabled={busyId === r.id}
                      className="col-span-2 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md py-2.5 disabled:opacity-60"
                      data-testid={`start-${r.id}`}
                    >
                      <Play size={14} /> Start trip
                    </button>
                  )}
                  {r.status === "in_progress" && (
                    <button
                      onClick={() => updateStatus(r.id, "completed")}
                      disabled={busyId === r.id}
                      className="col-span-2 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-md py-2.5 disabled:opacity-60"
                      data-testid={`complete-${r.id}`}
                    >
                      <CheckCircle2 size={14} /> Mark complete
                    </button>
                  )}
                  <button
                    onClick={() => { if (window.confirm("Cancel this ride?")) updateStatus(r.id, "canceled"); }}
                    disabled={busyId === r.id}
                    className="col-span-2 flex items-center justify-center gap-1.5 text-rose-600 border border-slate-200 hover:border-rose-300 hover:bg-rose-50 text-xs rounded-md py-2"
                    data-testid={`cancel-${r.id}`}
                  >
                    <XCircle size={12} /> Cancel ride
                  </button>
                </div>
              )}
              {r.status === "completed" && (
                <div className="mt-4">
                  <button
                    onClick={() => setRatingsRideId(r.id)}
                    className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm rounded-md py-2.5"
                    data-testid={`rate-${r.id}`}
                  >
                    <Star size={14} /> Rate rider
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
      {ratingsRideId && (
        <RatingsModal
          rideId={ratingsRideId}
          ride={rides.find((r) => r.id === ratingsRideId)}
          canRateAsDriver
          onClose={() => setRatingsRideId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
